import React, { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/styles";
import { gaps } from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import RestoreIcon from "@mui/icons-material/RestoreFromTrash";
import IosShareIcon from "@mui/icons-material/IosShare";
import { orderBy as _orderBy } from "lodash";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { useColumnSettings } from "utils/custom-hooks/useColumnSettings";
import { useCountyList } from "utils/custom-hooks/useCountyList";
import { usePolicyTypeOptions } from "utils/custom-hooks/usePolicyTypeOptions";
import { useFormPrefixOptions } from "utils/custom-hooks/useFormPrefixOptions";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { usePropertyTypeOptions } from "utils/custom-hooks/usePropertyTypeOptions";
import { useAsyncTextFilter } from "utils/custom-hooks/useAsyncTextFilter";
import { useTransCodeOptions } from "utils/custom-hooks/useTransCodeOptions";
import { useStateList } from "utils/custom-hooks/useStateList";
import {
  getFormNameListByText,
  getFileNumberListByText,
  getOriginalPolicyNumberListByText,
} from "utils/api/lookupApi";
import BulkActionBar, { BulkButton } from "../shared/BulkActionBar";
import ExportButton from "../shared/ExportButton";
import FileExportDialog from "controls/global/file-export-dialog";
import {
  exportPolicyTransaction,
  PolicyTransactionCriteriaDto,
  PolicyTransactionExportCriteriaDto,
  RestorePolicyTransactionResultDto,
} from "utils/api/policyTransactionApi";
import { showToast } from "controls/global/stewart-toast";
import DeletedRecordsTable from "./DeletedRecordsTable";
import { buildDrColumns, DR_GRID_NAME } from "./drColumns";

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const ToolbarRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: gaps.small1,
  flexWrap: "wrap",
});

interface DeletedRecordsTabProps {
  records: PolicyManagementRecord[];
  stateVals?: string[];
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  filters?: Record<string, string | string[] | number | null>;
  onFilterChange?: (name: string, value: string | string[] | number | null) => void;
  onResetFilters?: () => void;
  applyEnabled?: boolean;
  onApplyFilters?: () => void;
  onRefresh?: () => void;
  sortColumn?: string;
  onSortChange?: (next: string) => void;
  userTimeZone?: string;
  onRestore?: (selectedIds: string[]) => Promise<RestorePolicyTransactionResultDto | null>;
}

const DR_PROTECTED_FIELDS: (keyof PolicyManagementRecord)[] = [];

const DeletedRecordsTab = ({
  records,
  stateVals,
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  filters: filtersProp,
  onFilterChange,
  onResetFilters,
  applyEnabled,
  onApplyFilters,
  onRefresh,
  sortColumn,
  onSortChange,
  userTimeZone,
  onRestore,
}: DeletedRecordsTabProps) => {
  void filtersProp;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Bumped on Reset so the table (and its uncontrolled column-header inputs)
  // remounts with fresh empty state.
  const [resetCounter, setResetCounter] = useState(0);
  const [openFileExportModal, setOpenFileExportModal] = useState<boolean>(false);
  const [isExportAll, setIsExportAll] = useState<boolean>(true);
  const [isRestoring, setIsRestoring] = useState(false);

  const handleToggleRow = useCallback((record: PolicyManagementRecord) => {
    setSelectedIds((prev) =>
      prev.includes(record.uniqueIdentifier)
        ? prev.filter((id) => id !== record.uniqueIdentifier)
        : [...prev, record.uniqueIdentifier]
    );
  }, []);

  const handleToggleAllRows = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === records.length ? [] : records.map((r) => r.uniqueIdentifier)
    );
  }, [records]);

  const handleFiltersChange = useCallback(
    (name: string, value: string | number | null) => {
      onFilterChange?.(name, value);
    },
    [onFilterChange]
  );

  const handleMultiSelectChange = useCallback(
    (
      e: { target: { name: string; value: { value: string }[] } },
      value: { value: string }[]
    ) => {
      const fieldName = e.target.name;
      const selectedValues = value.map((v) => v.value);
      onFilterChange?.(
        fieldName,
        selectedValues.length > 0 ? selectedValues : null
      );
    },
    [onFilterChange]
  );

  const handleResetFilters = useCallback(() => {
    onResetFilters?.();
    setSelectedIds([]);
    setResetCounter((c) => c + 1);
  }, [onResetFilters]);

  const selectedCount = selectedIds.length;

  const { stateList } = useStateList();
  const stateOptions = useMemo(() => {
    if (stateVals && stateVals.length > 0) {
      return (stateList ?? []).filter((s) => stateVals.includes(s.value));
    }
    return stateList ?? [];
  }, [stateVals, stateList]);

  const productTypeOptions = usePolicyTypeOptions();
  const formPrefixOptions = useFormPrefixOptions();
  const transCodeOptions = useTransCodeOptions();
  const policyFormNameAsync = useAsyncTextFilter(getFormNameListByText);
  const fileNumberAsync = useAsyncTextFilter(getFileNumberListByText);
  const originalPolicyNumberAsync = useAsyncTextFilter(getOriginalPolicyNumberListByText);
  const recordTypeOptions = useRecordTypeOptions();
  const propertyTypeOptions = usePropertyTypeOptions();
  const countyOptions = useCountyList(stateVals);

  const defaultColumns = useMemo(
    () =>
      buildDrColumns({
        stateOptions,
        productTypeOptions,
        recordTypeOptions,
        propertyTypeOptions,
        countyOptions,
        formPrefixOptions,
        transCodeOptions,
        policyFormNameAsync,
        fileNumberAsync,
        originalPolicyNumberAsync,
        onFiltersChange: handleFiltersChange,
        onMultiSelectChange: handleMultiSelectChange,
      }),
    [
      stateOptions,
      productTypeOptions,
      recordTypeOptions,
      propertyTypeOptions,
      countyOptions,
      formPrefixOptions,
      transCodeOptions,
      policyFormNameAsync,
      fileNumberAsync,
      originalPolicyNumberAsync,
      handleFiltersChange,
      handleMultiSelectChange,
    ]
  );

  const {
    columns,
    hiddenColumns,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  } = useColumnSettings<PolicyManagementRecord>(
    DR_GRID_NAME,
    defaultColumns,
    DR_PROTECTED_FIELDS
  );

  const chooserOptions = useMemo<MenuColumnItem<PolicyManagementRecord>[]>(
    () =>
      defaultColumns.map((c) => ({
        field: c.field,
        name: c.name,
        checked: !hiddenColumns.includes(c.field),
      })),
    [defaultColumns, hiddenColumns]
  );

  const handleColumnChecked = useCallback(
    (field: keyof PolicyManagementRecord, wasChecked: boolean) => {
      setColumnHidden(field, wasChecked);
    },
    [setColumnHidden]
  );

  const exportedColumns = useMemo(
    () => _orderBy(columns.filter((c) => !hiddenColumns.includes(c.field)), "position").map((c) => c.field),
    [columns, hiddenColumns]
  );

  const buildExportPayload = useCallback(
    (exportAll: boolean): PolicyTransactionExportCriteriaDto => {
      const baseCriteria: Partial<PolicyTransactionCriteriaDto> = {
        UserTimeZone: userTimeZone,
        RequestedPage: page,
        // Deleted Records spans multiple queues — scoped by the IsDeleted flag
        IsDeleted: 1,
        SortColumn: "receivedDate",
      };

      const searchCriteria = exportAll
        ? {
            ...baseCriteria,
            ListCount: 0,
            PolicyTransactionIDList: null,
          }
        : {
            ...baseCriteria,
            ListCount: selectedIds.length,
            PolicyTransactionIDList: selectedIds.join(", "),
          };

      return {
        ExportColumns: exportedColumns,
        SearchCriteria: searchCriteria,
      };
    },
    [exportedColumns, selectedIds, userTimeZone, page]
  );

  const handleFileExport = async () => {
    if (selectedIds.length > 0) {
      setOpenFileExportModal(true);
    } else {
      const payload: PolicyTransactionExportCriteriaDto = buildExportPayload(isExportAll);
      await exportPolicyTransaction(payload);
    }
  };

  const handleRestore = useCallback(async () => {
    if (!onRestore || selectedCount === 0 || isRestoring) return;
    setIsRestoring(true);
    const idsToRestore = selectedIds;
    const result = await onRestore(idsToRestore);
    setIsRestoring(false);
    if (result && result.FailedCount === 0 && result.RestoredCount > 0) {
      showToast({ type: "success", message: "All records have been restored successfully." });
      setSelectedIds([]);
    } else if (result && result.RestoredCount > 0) {
      showToast({
        type: "error",
        message:
          "All records selected did not restore successfully due to system issue or a duplicate record currently exist.",
      });
      setSelectedIds(result.FailedPolicyTransactionIDList ?? []);
    } else {
      showToast({
        type: "error",
        message:
          "All records selected did not restore successfully due to system issue or a duplicate record currently exist.",
      });
    }
    onRefresh?.();
  }, [onRestore, selectedIds, selectedCount, isRestoring, onRefresh]);

  return (
    <TabContainer>
      <ToolbarRow>
        <ActionButton variant="outlined" color="secondary" onClick={handleResetFilters}>
          Reset
        </ActionButton>
        <ActionButton
          variant="outlined"
          color="primary"
          disabled={!applyEnabled}
          onClick={onApplyFilters}
        >
          Apply Filters
        </ActionButton>
        <Box sx={{ "& .MuiButton-root": { textTransform: "none" } }}>
          <ColumnsConfig
            options={chooserOptions}
            onChecked={handleColumnChecked}
            onShowAll={showAllColumns}
            onHideAll={hideAllColumns}
          />
        </Box>
        <ExportButton onClick={handleFileExport} />
      </ToolbarRow>

      <BulkActionBar open={selectedCount > 0} count={selectedCount}>
        <BulkButton
          icon={<RestoreIcon />}
          label="Restore"
          primary
          disabled={isRestoring}
          onClick={handleRestore}
        />
      </BulkActionBar>

      <DeletedRecordsTable
        key={resetCounter}
        records={records}
        selectedIds={selectedIds}
        onToggleRow={handleToggleRow}
        onToggleAllRows={handleToggleAllRows}
        columns={columns}
        hiddenColumns={hiddenColumns}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        onPageChange={onPageChange}
        totalRows={totalRows}
        totalPages={totalPages}
        isLoading={isLoading}
        sortColumn={sortColumn}
        onSortChange={onSortChange}
      />

      <FileExportDialog
        open={openFileExportModal}
        onExport={async (exportAll) => {
          setIsExportAll(exportAll);
          setOpenFileExportModal(false);
          const payload = buildExportPayload(exportAll);
          await exportPolicyTransaction(payload);
        }}
        onCancel={() => setOpenFileExportModal(false)}
        showSelectionPrompt={selectedIds?.some((s) => s)}
        selectionPrompt={"Include only the selected records or all records?"}
        icon={IosShareIcon}
      />
    </TabContainer>
  );
};

export default DeletedRecordsTab;
